import { Alert, Button, Checkbox, Flex, Form, Input, Result, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { useTheme } from "antd-style";
import { useLink, useLogin, useLogout } from "@refinedev/core";
import { usePageTitle } from "@/hooks/usePageTitle";
import { LockOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";

export const SSOLoginPage = ({ showRememberMe, showForgotPasswordLink, mode }) => {
  const { t } = useTranslation();
  const token = useTheme();
  const Link = useLink();
  const { mutateAsync: loginAsync, isLoading } = useLogin();
  const { mutateAsync: logoutAsync } = useLogout();
  const [mfa, setMfa] = useState(null);
  const [mfaFormInstance] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenantName = searchParams.get("tenant");
  const instanceId = searchParams.get("instance");
  const callbackURL = searchParams.get("callbackURL");
  const originalURL = searchParams.get("originalURL");

  const isValidUrl = tenantName && instanceId && callbackURL && originalURL;

  usePageTitle(t("auth.loginTitle", "VIA AIOps"));

  const { styles } = useStyles();

  const doLogin = async ({ username, password, remember }) => {
    const resp = await loginAsync({ tenant: tenantName, username, password, remember });
    if (resp.success && resp.challenge) {
      setMfa(resp);
    }
  };

  const doMfaLogin = async ({ code, remember }) => {
    const resp = await loginAsync({ code, remember, challenge: mfa.challenge });
    if (resp.success) {
      setMfa(null);
      // window.location.href = `${location.origin}/api/v1/um/login/idToken?target=${callbackURL}&redirect_to=${originalURL}`;
      // loginVIA({
      //   target: searchParams.get("callbackURL"),
      //   login: searchParams.get("originalURL"),
      // });
    } else {
      if (resp.error_code === 1 && resp.value.indexOf("expired") > -1) {
        mfaFormInstance.setFields([
          {
            name: "code",
            errors: [t("pages.login.errors.codeExpired", "code expired")],
          },
        ]);
      } else {
        mfaFormInstance.setFields([
          {
            name: "code",
            errors: [t("pages.login.errors.invalidCode", "Invalid code")],
          },
        ]);
      }
    }
  };

  const adminLoginForm = (
    <Form layout="vertical" size="middle" requiredMark={false} onFinish={doLogin} style={{ marginTop: 20 }}>
      {/* <Typography.Text style={{ marginBottom: 12 }}>Tenant: {tenantName}</Typography.Text> */}
      <Form.Item
        name="username"
        label={t("pages.login.fields.username", "Username")}
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredUsername", "Username is required"),
          },
        ]}
      >
        <Input prefix={<UserOutlined />} placeholder={t("pages.login.fields.username", "Username")} />
      </Form.Item>
      <Form.Item
        name="password"
        label={t("pages.login.fields.password", "Password")}
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredPassword", "Password is required"),
          },
        ]}
      >
        <Input prefix={<LockOutlined />} type="password" autoComplete="current-password" placeholder="●●●●●●●●" />
      </Form.Item>
      {(showRememberMe || showForgotPasswordLink) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          {showRememberMe && (
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox
                style={{
                  fontSize: "12px",
                }}
              >
                {t("pages.login.buttons.rememberMe", "Remember me")}
              </Checkbox>
            </Form.Item>
          )}
          {showForgotPasswordLink && (
            <Link
              style={{
                color: token.colorPrimaryTextHover,
                fontSize: "12px",
                marginLeft: "auto",
              }}
              to="/forgot-password"
            >
              {t("pages.login.buttons.forgotPassword", "Forgot password?")}
            </Link>
          )}
        </div>
      )}
      <Form.Item style={{ marginBottom: 12 }}>
        <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
          {t("pages.login.signin", "Sign in")}
        </Button>
      </Form.Item>
    </Form>
  );

  const mfaForm = (
    <Form
      form={mfaFormInstance}
      layout="vertical"
      requiredMark={false}
      onFinish={(values) => doMfaLogin({ ...values, remember: true })}
      style={{ marginTop: 24 }}
    >
      <Form.Item>
        <Typography.Text>{t("pages.login.mfaDescription", "Enter the code sent to your email box")}</Typography.Text>
      </Form.Item>
      <Form.Item
        name="code"
        label={t("pages.login.fields.code", "Code")}
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredCode", "Code is required"),
          },
        ]}
      >
        <Input placeholder={t("pages.login.fields.code", "Code")} />
      </Form.Item>
      <Form.Item style={{ marginBottom: 12 }}>
        <Flex gap="middle" justify="space-between">
          <Button size="large" loading={isLoading} block onClick={() => setMfa(null)}>
            {t("pages.login.buttons.back", "Back")}
          </Button>
          <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
            {t("pages.login.buttons.continue", "Continue")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );

  let content = null;
  if (mode === "noValidRole") {
    content = (
      <div style={{ marginTop: 12, textAlign: "center" }}>
        <Alert
          type="warning"
          showIcon
          style={{ textAlign: "left" }}
          description="Access to this instance is restricted. Please reach out to your system administrator for assistance."
        />
        <Button
          type="primary"
          style={{ marginTop: 16 }}
          onClick={async () => {
            await logoutAsync();
            // window.location.reload();
          }}
        >
          Sign in with another account
        </Button>
        <Button type="link" style={{ marginTop: 12 }} onClick={() => navigate("/")}>
          Go to user console
        </Button>
      </div>
    );
  } else if (mfa && isValidUrl) {
    content = null;
  } else if (isValidUrl) {
    content = adminLoginForm;
  } else {
    content = (
      <Result
        status="error"
        iconFontSize={20}
        title={t("pages.login.invalidRequestTitle", "Invalid Request")}
        subTitle={t("pages.login.invalidRequestSubtitle", "The request URI is not a valid SSO login URI.")}
        extra={[
          <Button type="primary" key="console" target="/login" href="/login">
            {t("pages.login.buttons.loginConsole", "Login Console")}
          </Button>,
        ]}
      ></Result>
    );
  }

  return (
    <div className={styles.loginContainer}>
      <div className={styles.formContainer}>
        <div className={styles.pageTitle}>
          {/* <AIOpsLogo /> */}
          <span style={{ color: token.colorPrimary, fontSize: "28px", fontWeight: 700 }}>
            {t("auth.loginFormTitle", "VIA AIOps")}
          </span>
        </div>
        {isValidUrl && (
          <div style={{ fontSize: 18, textAlign: "center", fontWeight: 700 }}>Login To Tenant: {tenantName} </div>
        )}
        {mode !== "noValidRole" && mfa && isValidUrl && mfaForm}
        {content}
      </div>
    </div>
  );
};
